import { Box, Tab, Tabs, Typography } from '@mui/material';
import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';

import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import { Dropdown, ToggleButton } from '@commons/utils/styledLibraryComponents';
import { fetchGroupsByStoreIds } from '@orders/OrderAnalyticsModulesContainer/utils/fetches';
import {
  GenericContentContainer,
  GenericGapContainer,
  GenericPageContainer,
} from '@commons/Layout/styledComponents';
import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';
import { getUserTimezone } from '@commons/utils/date';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { PeriodDatePicker, DEFAULT_PRESETS } from '@commons/DatePickers/PeriodDatePicker';
import { showErrorMessage } from '@actions/messageconfirmation';
import { TabPanel } from '@commons/TabPanel';
import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

// Import the panel components
import {
  DISPLAY_FORMAT_IS_PERCENTAGE,
  METRIC_TYPE_OPTIONS,
} from './components/ChannelPanel/utils/config';
import { fetchGlobalData } from './components/GlobalPanel/utils/fetch';
import ChannelPanel from './components/ChannelPanel';
import GlobalPanel from './components/GlobalPanel';
import ProductPanel from './components/ProductPanel';

const getLastYearSameWeekday = (date) => date.clone().subtract(364, 'days');

const Performance = (props) => {
  const { showErrorMessage, stores, match, enableChannelPanel, enableTicketsNumber } = props;

  const USER_TIMEZONE = getUserTimezone();
  const TODAY = moment.tz(USER_TIMEZONE);

  // Header states
  const [displayedStores, setDisplayedStores] = useState(stores || []);
  const [selectedStores, setSelectedStores] = useState(stores || []);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Tabs management
  const [activeTab, setActiveTab] = useState(0);

  // Global data state (shared between panels)
  const [globalData, setGlobalData] = useState(null);
  const [globalTableData, setGlobalTableData] = useState([]);
  const [isLoadingGlobal, setIsLoadingGlobal] = useState(false);

  // Channel panel filters state
  const [selectedChannelMetric, setSelectedChannelMetric] = useState(METRIC_TYPE_OPTIONS()[0]);
  const [selectedDisplayIsPercentage, setSelectedDisplayFormat] = useState(
    DISPLAY_FORMAT_IS_PERCENTAGE[0],
  );

  // Date pickers states
  const defaultCurrentStartDate = TODAY.clone().subtract(7, 'days');
  const defaultCurrentEndDate = TODAY.clone().subtract(1, 'days');

  const currentPeriod = usePeriodDatePickerState(
    defaultCurrentStartDate,
    defaultCurrentEndDate,
    USER_TIMEZONE,
  );

  const previousPeriod = usePeriodDatePickerState(
    getLastYearSameWeekday(defaultCurrentStartDate),
    getLastYearSameWeekday(defaultCurrentEndDate),
    USER_TIMEZONE,
  );

  // Custom handler to update previous period when current period changes
  const handleCurrentPeriodDatesChange = (dates, isReset = false) => {
    if (dates.startDate && dates.endDate) {
      const previousStartDate = getLastYearSameWeekday(dates.startDate);
      const previousEndDate = getLastYearSameWeekday(dates.endDate);

      previousPeriod.handleSelectedDates(
        {
          startDate: previousStartDate,
          endDate: previousEndDate,
        },
        true,
      );
    }
    // Previous date picker must be updated before current date picker to avoid race condition

    currentPeriod.handleSelectedDates(dates, isReset);
  };

  const loadGlobalData = async () => {
    setIsLoadingGlobal(true);

    try {
      const { globalData, globalTableData } = await fetchGlobalData(
        selectedStores,
        currentPeriod,
        previousPeriod,
      );

      setGlobalData(globalData);
      setGlobalTableData(globalTableData);
    } catch (error) {
      showErrorMessage(i18next.t('GENERAL.GLOBAL_FETCH_ERROR'));
      setGlobalData({});
      setGlobalTableData([]);
    } finally {
      setIsLoadingGlobal(false);
    }
  };

  // Fetch global data (shared between GlobalPanel and ProductPanel)
  useEffect(() => {
    if (
      selectedStores.length &&
      currentPeriod.startDate &&
      currentPeriod.endDate &&
      previousPeriod.startDate &&
      previousPeriod.endDate &&
      !isLoadingGlobal
    )
      loadGlobalData();
  }, [
    selectedStores,
    currentPeriod.startDate,
    currentPeriod.endDate,
    previousPeriod.startDate,
    previousPeriod.endDate,
  ]);

  useEffect(() => {
    if (!stores || !stores.length) {
      return;
    }

    (async () => {
      const groups = await fetchGroupsByStoreIds(
        stores.map(({ id }) => id),
        showErrorMessage,
      );

      setGroups(groups);
    })();
  }, []);

  // If stores are not loaded yet, show loading
  if (!stores.length) {
    return (
      <GenericPageContainer>
        <GenericContentContainer>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '50vh',
            }}
          >
            <DeepsightComponentLoader />
          </Box>
        </GenericContentContainer>
      </GenericPageContainer>
    );
  }

  return (
    <GenericPageContainer>
      <NavigationBreadCrumb featurePath={match.path} />
      <GenericContentContainer padding="0 24px">
        {/* Header */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#f8f8f8',
            zIndex: 10,
            paddingBottom: '0px',
            paddingTop: '16px',
          }}
        >
          <Box>
            <LeftRightSplitter
              left={
                <GenericGapContainer>
                  {!!groups.length && (
                    <Dropdown
                      iconSrc="/images/inpulse/group-dmgrey-small.svg"
                      isUniqueSelection={false}
                      items={groups}
                      searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                      selectedItems={selectedGroups}
                      onSelectionChange={(items) => {
                        setSelectedGroups(items);

                        if (!items.length) {
                          // Reset selected stores and displayed stores with stores from redux
                          setSelectedStores(stores);
                          setDisplayedStores(stores);
                          return;
                        }

                        const groupsStoreIds = items.flatMap(({ storeIds }) => storeIds);
                        const filteredStores = stores.filter(({ id }) =>
                          groupsStoreIds.includes(id),
                        );

                        setSelectedStores(filteredStores);
                        setDisplayedStores(filteredStores);
                      }}
                    />
                  )}

                  <Dropdown
                    iconSrc="/images/inpulse/pin-black-small.svg"
                    isUniqueSelection={false}
                    items={displayedStores || []}
                    searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                    selectedItems={selectedStores}
                    onSelectionChange={setSelectedStores}
                  />

                  <PeriodDatePicker
                    endDate={currentPeriod.endDate}
                    focusedDateInput={currentPeriod.focusedDateInput}
                    maxFutureDate={TODAY.clone().subtract(1, 'days')}
                    presets={DEFAULT_PRESETS}
                    setFocusedDateInput={currentPeriod.setFocusedDateInput}
                    startDate={currentPeriod.startDate}
                    timezone={USER_TIMEZONE}
                    onDatesChange={handleCurrentPeriodDatesChange}
                  />

                  <Typography variant="body1">{i18next.t('ANALYSIS.PERFORMANCE.VS')}</Typography>

                  <PeriodDatePicker
                    endDate={previousPeriod.endDate}
                    focusedDateInput={previousPeriod.focusedDateInput}
                    maxFutureDate={TODAY.clone()}
                    setFocusedDateInput={previousPeriod.setFocusedDateInput}
                    showQuickSelection={false}
                    startDate={previousPeriod.startDate}
                    timezone={USER_TIMEZONE}
                    onDatesChange={previousPeriod.handleSelectedDates}
                  />

                  {activeTab === 2 && (
                    <>
                      {enableTicketsNumber && (
                        <ToggleButton
                          choices={METRIC_TYPE_OPTIONS()}
                          customStyle={{ width: '80px', height: '38px' }}
                          isDisabled={false}
                          selectedChoice={selectedChannelMetric}
                          setSelectedChoice={setSelectedChannelMetric}
                        />
                      )}

                      <ToggleButton
                        choices={DISPLAY_FORMAT_IS_PERCENTAGE}
                        customStyle={{ width: '80px', height: '38px' }}
                        isDisabled={false}
                        selectedChoice={selectedDisplayIsPercentage}
                        setSelectedChoice={setSelectedDisplayFormat}
                      />
                    </>
                  )}
                </GenericGapContainer>
              }
              right={null}
            />
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label={i18next.t('ANALYSIS.PERFORMANCE.GLOBAL_TAB_TITLE')} />
              <Tab label={i18next.t('ANALYSIS.PERFORMANCE.PRODUCT_TAB_TITLE')} />
              {enableChannelPanel && (
                <Tab label={i18next.t('ANALYSIS.PERFORMANCE.CHANNEL_TAB_TITLE')} />
              )}
            </Tabs>
          </Box>
        </Box>

        {/* Tab Content */}
        <TabPanel index={0} value={activeTab}>
          {/* Global Tab */}
          <GlobalPanel
            currentPeriod={currentPeriod}
            enableTicketsNumber={enableTicketsNumber}
            globalData={globalData}
            globalTableData={globalTableData}
            isLoading={isLoadingGlobal}
            previousPeriod={previousPeriod}
            selectedStores={selectedStores}
          />
        </TabPanel>

        <TabPanel index={1} value={activeTab}>
          {/* Product Tab */}
          <ProductPanel
            currentPeriod={currentPeriod}
            globalData={globalData}
            isLoadingGlobal={isLoadingGlobal}
            previousPeriod={previousPeriod}
            selectedStores={selectedStores}
          />
        </TabPanel>

        {enableChannelPanel && (
          <TabPanel index={2} value={activeTab}>
            {/* Sales Channel Tab */}
            <ChannelPanel
              currentPeriod={currentPeriod}
              isPercentage={selectedDisplayIsPercentage.id}
              previousPeriod={previousPeriod}
              selectedMetric={selectedChannelMetric}
              selectedStores={selectedStores}
            />
          </TabPanel>
        )}
      </GenericContentContainer>
    </GenericPageContainer>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  stores: getSalesPointStores(state.baseReducer.activeStores),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Performance);
